/**
 * Alshallal Pastry Company HR System - OCR Processing Dialog
 * Integrated OCR processing workflow after document upload
 * 
 * Flow:
 * 1. Prompt: Ask if user wants to apply OCR
 * 2. Processing: Run OCR and show progress
 * 3. Review: Show extracted data for confirmation
 * 4. Auto-fill: Apply data and get final confirmation
 */
import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSearch,
  Edit,
  Check,
  X,
  RefreshCw,
  Eye,
  Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// OCR Dialog Steps
const STEPS = {
  PROMPT: 'prompt',
  PROCESSING: 'processing',
  REVIEW: 'review',
  CONFIRM: 'confirm',
  COMPLETE: 'complete',
  ERROR: 'error'
};

// Confidence level styling
const getConfidenceBadge = (confidence) => {
  if (confidence >= 0.9) return { bg: 'bg-green-100 text-green-800', label: 'High' };
  if (confidence >= 0.7) return { bg: 'bg-amber-100 text-amber-800', label: 'Medium' };
  return { bg: 'bg-red-100 text-red-800', label: 'Low' };
};

const OCRProcessingDialog = ({
  open,
  onOpenChange,
  document,
  token,
  isRTL = false,
  onComplete,
  onSkip
}) => {
  const [step, setStep] = useState(STEPS.PROMPT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [reuploading, setReuploading] = useState(false);
  const fileInputRef = useRef(null);
  const [ocrResults, setOcrResults] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [confirmReason, setConfirmReason] = useState('');
  const [acceptedFields, setAcceptedFields] = useState({});

  // Reset dialog state
  const resetDialog = useCallback(() => {
    setStep(STEPS.PROMPT);
    setLoading(false);
    setError(null);
    setErrorCode(null);
    setReuploading(false);
    setOcrResults(null);
    setEditingField(null);
    setEditValue('');
    setConfirmReason('');
    setAcceptedFields({});
  }, []);

  // Handle dialog close
  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  // Skip OCR processing
  const handleSkip = () => {
    handleClose();
    if (onSkip) onSkip();
  };

  // Trigger OCR processing
  const triggerOCR = async () => {
    if (!document?.id) return;

    setLoading(true);
    setError(null);
    setStep(STEPS.PROCESSING);

    try {
      const response = await axios.post(
        `${API_URL}/documents/${document.id}/ocr/trigger`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.job) {
        setOcrResults(response.data.job);
        setStep(STEPS.REVIEW);
      } else {
        throw new Error(response.data.message || 'OCR processing failed');
      }
    } catch (err) {
      console.error('OCR trigger failed:', err);
      setError(err.response?.data?.detail?.error || err.message || 'OCR processing failed');
      setErrorCode(err.response?.data?.detail?.code || null);
      setStep(STEPS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Re-upload the original file for a document whose bytes are missing
  // (SOURCE_FILE_MISSING), then automatically re-run OCR on the fresh file.
  const handleReuploadFile = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';  // allow re-picking the same file
    if (!file || !document?.id) return;

    setReuploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(
        `${API_URL}/documents/${document.id}/replace-file`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      // File restored — re-run OCR immediately.
      setErrorCode(null);
      await triggerOCR();
    } catch (err) {
      console.error('Re-upload failed:', err);
      setError(err.response?.data?.detail?.error || err.message || 'Re-upload failed');
      setStep(STEPS.ERROR);
    } finally {
      setReuploading(false);
    }
  };

  // Fetch existing OCR results
  const fetchOCRResults = async () => {
    if (!document?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API_URL}/documents/${document.id}/ocr`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.has_ocr && response.data.job) {
        setOcrResults(response.data.job);
        setStep(STEPS.REVIEW);
      } else {
        // No existing OCR, trigger new one
        await triggerOCR();
      }
    } catch (err) {
      console.error('Fetch OCR failed:', err);
      // No OCR exists, trigger new one
      await triggerOCR();
    } finally {
      setLoading(false);
    }
  };

  // Accept a field suggestion
  const acceptField = async (fieldName, suggestion) => {
    if (confirmReason.length < 10) {
      setError(isRTL ? 'يرجى إدخال سبب (10 أحرف على الأقل)' : 'Please enter a reason (at least 10 characters)');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/documents/${document.id}/ocr/suggestions/${fieldName}/accept`,
        { reason: confirmReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAcceptedFields(prev => ({
        ...prev,
        [fieldName]: { value: suggestion.value, status: 'accepted' }
      }));
      setConfirmReason('');
      setError(null);
    } catch (err) {
      console.error('Accept failed:', err);
      setError(err.response?.data?.detail?.error || 'Failed to accept suggestion');
    } finally {
      setLoading(false);
    }
  };

  // Edit a field suggestion
  const editField = async (fieldName) => {
    if (confirmReason.length < 10 || !editValue.trim()) {
      setError(isRTL ? 'يرجى إدخال قيمة وسبب (10 أحرف على الأقل)' : 'Please enter a value and reason (at least 10 characters)');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/documents/${document.id}/ocr/suggestions/${fieldName}/edit`,
        { new_value: editValue.trim(), reason: confirmReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAcceptedFields(prev => ({
        ...prev,
        [fieldName]: { value: editValue.trim(), status: 'edited' }
      }));
      setEditingField(null);
      setEditValue('');
      setConfirmReason('');
      setError(null);
    } catch (err) {
      console.error('Edit failed:', err);
      setError(err.response?.data?.detail?.error || 'Failed to edit suggestion');
    } finally {
      setLoading(false);
    }
  };

  // Reject a field suggestion
  const rejectField = async (fieldName) => {
    if (confirmReason.length < 10) {
      setError(isRTL ? 'يرجى إدخال سبب (10 أحرف على الأقل)' : 'Please enter a reason (at least 10 characters)');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/documents/${document.id}/ocr/suggestions/${fieldName}/reject`,
        { reason: confirmReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAcceptedFields(prev => ({
        ...prev,
        [fieldName]: { value: null, status: 'rejected' }
      }));
      setConfirmReason('');
      setError(null);
    } catch (err) {
      console.error('Reject failed:', err);
      setError(err.response?.data?.detail?.error || 'Failed to reject suggestion');
    } finally {
      setLoading(false);
    }
  };

  // Check if all suggestions have been processed
  const allProcessed = ocrResults?.suggestions?.every(
    s => acceptedFields[s.field_name]
  );

  // Final confirmation
  const handleFinalConfirm = () => {
    setStep(STEPS.COMPLETE);
    setTimeout(() => {
      handleClose();
      if (onComplete) onComplete(acceptedFields);
    }, 1500);
  };

  // Render based on current step
  const renderContent = () => {
    switch (step) {
      case STEPS.PROMPT:
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSearch className="h-5 w-5" />
                {isRTL ? 'معالجة OCR/LLM' : 'OCR/LLM Processing'}
              </DialogTitle>
              <DialogDescription>
                {isRTL
                  ? 'هل تريد تطبيق OCR/LLM لاستخراج البيانات تلقائيًا من المستند؟'
                  : 'Would you like to apply OCR/LLM to automatically extract data from this document?'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="py-6 text-center">
              <FileSearch className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                {isRTL
                  ? 'سيقوم النظام بتحليل المستند واستخراج المعلومات مثل التواريخ والأرقام والأسماء.'
                  : 'The system will analyze the document and extract information like dates, numbers, and names.'
                }
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleSkip}>
                {isRTL ? 'تخطي' : 'Skip'}
              </Button>
              <Button onClick={fetchOCRResults}>
                <FileSearch className="h-4 w-4 me-2" />
                {isRTL ? 'تطبيق OCR' : 'Apply OCR'}
              </Button>
            </DialogFooter>
          </>
        );

      case STEPS.PROCESSING:
        return (
          <>
            <DialogHeader>
              <DialogTitle>{isRTL ? 'جاري المعالجة...' : 'Processing...'}</DialogTitle>
            </DialogHeader>

            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isRTL ? 'جاري تحليل المستند واستخراج البيانات...' : 'Analyzing document and extracting data...'}
              </p>
            </div>
          </>
        );

      case STEPS.REVIEW:
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {isRTL ? 'مراجعة نتائج OCR' : 'Review OCR Results'}
              </DialogTitle>
              <DialogDescription>
                {isRTL
                  ? 'راجع البيانات المستخرجة وقم بقبول أو تعديل أو رفض كل حقل.'
                  : 'Review the extracted data and accept, edit, or reject each field.'
                }
              </DialogDescription>
            </DialogHeader>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="max-h-[400px] overflow-y-auto space-y-4 py-4">
              {ocrResults?.suggestions?.length > 0 ? (
                ocrResults.suggestions.map((suggestion) => {
                  const confidence = getConfidenceBadge(suggestion.confidence);
                  const fieldStatus = acceptedFields[suggestion.field_name];
                  const isEditing = editingField === suggestion.field_name;

                  return (
                    <div
                      key={suggestion.field_name}
                      className={cn(
                        "p-4 border rounded-lg",
                        fieldStatus?.status === 'accepted' && "bg-green-50 border-green-200",
                        fieldStatus?.status === 'edited' && "bg-blue-50 border-blue-200",
                        fieldStatus?.status === 'rejected' && "bg-red-50 border-red-200"
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Label className="font-medium capitalize">
                            {suggestion.field_name.replace(/_/g, ' ')}
                          </Label>
                          <Badge className={confidence.bg}>{confidence.label}</Badge>
                          {suggestion.is_high_risk && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              {isRTL ? 'حقل حساس' : 'Sensitive'}
                            </Badge>
                          )}
                        </div>
                        {fieldStatus && (
                          <Badge className={cn(
                            fieldStatus.status === 'accepted' && "bg-green-100 text-green-800",
                            fieldStatus.status === 'edited' && "bg-blue-100 text-blue-800",
                            fieldStatus.status === 'rejected' && "bg-red-100 text-red-800"
                          )}>
                            {fieldStatus.status === 'accepted' && (isRTL ? 'مقبول' : 'Accepted')}
                            {fieldStatus.status === 'edited' && (isRTL ? 'معدل' : 'Edited')}
                            {fieldStatus.status === 'rejected' && (isRTL ? 'مرفوض' : 'Rejected')}
                          </Badge>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="space-y-2">
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={isRTL ? 'القيمة الجديدة' : 'New value'}
                          />
                          <Textarea
                            value={confirmReason}
                            onChange={(e) => setConfirmReason(e.target.value)}
                            placeholder={isRTL ? 'سبب التعديل (10 أحرف على الأقل)' : 'Reason for edit (min 10 chars)'}
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => editField(suggestion.field_name)} disabled={loading}>
                              <Check className="h-3 w-3 me-1" />
                              {isRTL ? 'حفظ' : 'Save'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingField(null);
                              setEditValue('');
                              setConfirmReason('');
                            }}>
                              <X className="h-3 w-3 me-1" />
                              {isRTL ? 'إلغاء' : 'Cancel'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="bg-muted p-2 rounded text-sm mb-2 font-mono">
                            {fieldStatus?.value || suggestion.value || '—'}
                          </div>

                          {!fieldStatus && (
                            <div className="space-y-2">
                              <Textarea
                                value={confirmReason}
                                onChange={(e) => setConfirmReason(e.target.value)}
                                placeholder={isRTL ? 'سبب القرار (10 أحرف على الأقل)' : 'Reason for decision (min 10 chars)'}
                                rows={2}
                                className="text-sm"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => acceptField(suggestion.field_name, suggestion)}
                                  disabled={loading || confirmReason.length < 10}
                                >
                                  <CheckCircle className="h-3 w-3 me-1" />
                                  {isRTL ? 'قبول' : 'Accept'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingField(suggestion.field_name);
                                    setEditValue(suggestion.value || '');
                                  }}
                                >
                                  <Edit className="h-3 w-3 me-1" />
                                  {isRTL ? 'تعديل' : 'Edit'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => rejectField(suggestion.field_name)}
                                  disabled={loading || confirmReason.length < 10}
                                >
                                  <XCircle className="h-3 w-3 me-1" />
                                  {isRTL ? 'رفض' : 'Reject'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>{isRTL ? 'لم يتم استخراج أي بيانات من المستند' : 'No data was extracted from the document'}</p>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleSkip}>
                {isRTL ? 'إغلاق' : 'Close'}
              </Button>
              {allProcessed && ocrResults?.suggestions?.length > 0 && (
                <Button onClick={handleFinalConfirm}>
                  <CheckCircle className="h-4 w-4 me-2" />
                  {isRTL ? 'تأكيد نهائي' : 'Final Confirm'}
                </Button>
              )}
            </DialogFooter>
          </>
        );

      case STEPS.COMPLETE:
        return (
          <>
            <DialogHeader>
              <DialogTitle>{isRTL ? 'تم بنجاح!' : 'Complete!'}</DialogTitle>
            </DialogHeader>

            <div className="py-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isRTL ? 'تم تطبيق البيانات المستخرجة بنجاح' : 'Extracted data has been applied successfully'}
              </p>
            </div>
          </>
        );

      case STEPS.ERROR:
        return (
          <>
            <DialogHeader>
              <DialogTitle className="text-red-600">
                {isRTL ? 'خطأ في المعالجة' : 'Processing Error'}
              </DialogTitle>
            </DialogHeader>

            <div className="py-8 text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4" data-testid="ocr-error-message">{error}</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="hidden"
              onChange={handleReuploadFile}
              data-testid="ocr-reupload-input"
            />

            {errorCode === 'SOURCE_FILE_MISSING' ? (
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={handleSkip} disabled={reuploading}>
                  {isRTL ? 'إغلاق' : 'Close'}
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={reuploading}
                  data-testid="ocr-reupload-btn"
                >
                  {reuploading ? (
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 me-2" />
                  )}
                  {reuploading
                    ? (isRTL ? 'جارٍ الرفع…' : 'Uploading…')
                    : (isRTL ? 'إعادة رفع الملف' : 'Re-upload file')}
                </Button>
              </DialogFooter>
            ) : (
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={handleSkip}>
                  {isRTL ? 'إغلاق' : 'Close'}
                </Button>
                <Button onClick={() => { setStep(STEPS.PROMPT); setError(null); }} data-testid="ocr-retry-btn">
                  <RefreshCw className="h-4 w-4 me-2" />
                  {isRTL ? 'إعادة المحاولة' : 'Retry'}
                </Button>
              </DialogFooter>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default OCRProcessingDialog;
